import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type WhosNewSortDirection = "asc" | "desc";

// Tab values used by the Tree View month filter pills (ABOT-12352).
// Not consumed by the list view — the list always shows the last 12 months.
export enum WhosNewReportTabs {
  THIS_MONTH = "this_month_tab",
  LAST_3 = "last_three_months_tab",
  LAST_6 = "last_six_months_tab",
  LAST_9 = "last_nine_months_tab",
  LAST_12 = "last_twelve_months_tab",
}

/**
 * Redux state interface for Who's New Action Report.
 *
 * @interface WhosNewARState
 * @property {WhosNewReportTabs} activeView - Currently active Tree View month filter (ABOT-12352)
 * @property {string[]} affAbosToExpand - Array of AffAbo identifiers (format: "aff-abo") to auto-expand in tree view
 * @property {WhosNewSortDirection} sortDirection - Current sort direction for period groups
 * @property {string[]} availableKpis - List of available KPIs for the report
 */
interface WhosNewARState {
  activeView: WhosNewReportTabs;
  affAbosToExpand: string[];
  sortDirection: WhosNewSortDirection;
  availableKpis: string[];
}

export const initialState: WhosNewARState = {
  activeView: WhosNewReportTabs.LAST_12,
  affAbosToExpand: [],
  sortDirection: "desc",
  availableKpis: [],
};

const { actions, reducer } = createSlice({
  name: "whosNewActionReport",
  initialState,
  reducers: {
    setActiveTabView: (state, action: PayloadAction<WhosNewReportTabs>) => {
      const { payload } = action;
      return {
        ...state,
        activeView: payload,
      };
    },
    /**
     * Sets the list of AffAbo identifiers to auto-expand in tree view.
     *
     * When tree view is activated, this action stores the list of ABOs
     * currently displayed in the table report. The tree component subscribes
     * to this state and automatically expands nodes to reveal these ABOs.
     *
     * @param {string[]} action.payload - Array of AffAbo identifiers in format "aff-abo" (e.g., ["123-456", "789-012"])
     */
    setAffAbosToExpand: (state, action: PayloadAction<string[]>) => {
      const { payload } = action;
      return {
        ...state,
        affAbosToExpand: payload,
      };
    },
    setAvailableKpis: (state, action: PayloadAction<string[]>) => {
      const { payload } = action;
      return {
        ...state,
        availableKpis: payload,
      };
    },
    toggleSortDirection: (state) => {
      state.sortDirection = state.sortDirection === "asc" ? "desc" : "asc";
    },
  },
});

export const { setActiveTabView, setAffAbosToExpand, setAvailableKpis, toggleSortDirection } = actions;

export default reducer;
